import { Building2, Handshake, HelpCircle, LayoutDashboard, Package, Target, Users } from 'lucide-react';
import type { NavigationSection } from './navigation';

interface SupplierNavigationContext {
  /** Owner or admin of the supplier org. A salesman does not see Sælgere. */
  isAdmin: boolean;
}

/** Every supplier route lives under this prefix, except the Oversigt landing at "/", /help and /nyheder. */
export const SUPPLIER_ROUTE_PREFIX = '/supplier';

/**
 * The supplier org's own side menu (the account manager's home), as in the approved
 * mockup: Salg · Leverandør · System. Pure so the admin/salesman split is unit-testable.
 */
export function getSupplierNavigationSections({ isAdmin }: SupplierNavigationContext): NavigationSection[] {
  return [
    {
      title: 'Salg',
      items: [
        { label: 'Oversigt', path: '/', icon: LayoutDashboard },
        { label: 'Kunder', path: `${SUPPLIER_ROUTE_PREFIX}/customers`, icon: Building2 },
        { label: 'Mål', path: `${SUPPLIER_ROUTE_PREFIX}/goals`, icon: Target },
      ],
    },
    {
      title: 'Leverandør',
      items: [
        { label: 'Sortiment', path: `${SUPPLIER_ROUTE_PREFIX}/catalog`, icon: Package },
        { label: 'Henvisninger', path: `${SUPPLIER_ROUTE_PREFIX}/referrals`, icon: Handshake },
      ],
    },
    {
      title: 'System',
      items: [
        ...(isAdmin ? [{ label: 'Sælgere', path: `${SUPPLIER_ROUTE_PREFIX}/settings`, icon: Users }] : []),
        { label: 'Hjælp', path: '/help', icon: HelpCircle },
      ],
    },
  ];
}

/** Where a signed-in supplier-org user may be: the landing, /supplier/*, help, Nyt i Mesterplan, profile and privacy. */
export function isSupplierShellPath(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname === SUPPLIER_ROUTE_PREFIX ||
    pathname.startsWith(`${SUPPLIER_ROUTE_PREFIX}/`) ||
    pathname === '/help' ||
    pathname === '/nyheder' ||
    pathname === '/profile' ||
    pathname.startsWith('/profile/') ||
    pathname === '/privacy-policy'
  );
}

/** Routes an account manager may open inside a customer: the capped menu's pages and their subroutes. */
const SUPPLIER_SESSION_ROOTS = ['/projects', '/quotes', '/walkthroughs', '/ai-analyse', '/price-library', '/efaktura', '/help', '/nyheder', '/privacy-policy'];

export function isSupplierSessionPath(pathname: string): boolean {
  return SUPPLIER_SESSION_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}

/** An address under /supplier (the supplier shell's own pages). */
export function isSupplierAddress(pathname: string): boolean {
  return pathname === SUPPLIER_ROUTE_PREFIX || pathname.startsWith(`${SUPPLIER_ROUTE_PREFIX}/`);
}

/**
 * What a /supplier/* address (a reload or a pasted link) does while no org is active yet. On the first render the org
 * is restored from storage only after the org list has loaded, so the shell would still look like a company shell and
 * bounce the address to "/". `wait` holds the page on the loading screen; `retry` is the error state with "Prøv igen"
 * for an org list that could not be read (otherwise the spinner would never end); `proceed` is the normal path. A user
 * whose list came back empty has no org to wait for and is not held.
 */
export function supplierOrgGate(args: {
  pathname: string;
  activeOrgId: string | null;
  isLoadingCompanies: boolean;
  hasCompanies: boolean;
  orgsFailed: boolean;
}): 'wait' | 'retry' | 'proceed' {
  const { pathname, activeOrgId, isLoadingCompanies, hasCompanies, orgsFailed } = args;
  if (activeOrgId !== null || !isSupplierAddress(pathname)) return 'proceed';
  if (orgsFailed) return 'retry';
  return isLoadingCompanies || hasCompanies ? 'wait' : 'proceed';
}

/**
 * Where a stray URL goes for the active shell, or null to render it. A supplier org only has
 * its own pages; a supplier session only the capped menu's (landing is /projects); everyone
 * else never sees /supplier/*.
 */
export function resolveShellRedirect(args: {
  isSupplierOrg: boolean;
  isSupplierSession: boolean;
  pathname: string;
}): string | null {
  const { isSupplierOrg, isSupplierSession, pathname } = args;
  if (isSupplierOrg) return isSupplierShellPath(pathname) ? null : '/';
  if (isSupplierSession) return isSupplierSessionPath(pathname) ? null : '/projects';
  return pathname === SUPPLIER_ROUTE_PREFIX || pathname.startsWith(`${SUPPLIER_ROUTE_PREFIX}/`) ? '/' : null;
}
